import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Checkbox,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import useAuthorize from '../hooks/useAuthorize';
import { WHATSAPP_MODULE_ROLES } from '../roles/portalRoles';
import TemplatePreviewPanel from './TemplatePreviewPanel';
import AudiencePickerModal from './AudiencePickerModal';
import {
  createBroadcastJob,
  fetchTemplate,
  fetchTemplates,
  previewAudience,
} from './whatsappBroadcastApi';
import { AudienceFilters, BroadcastProvider, WhatsAppTemplate } from './types';

const steps = ['Gateway', 'Template', 'Audience', 'Review'];

const WhatsAppBroadcastCreate = () => {
  useAuthorize(WHATSAPP_MODULE_ROLES);
  const notify = useNotify();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [provider, setProvider] = useState<BroadcastProvider>('twilio');
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [preview, setPreview] = useState('');
  const [audienceFilters, setAudienceFilters] = useState<AudienceFilters>({});
  const [audienceCount, setAudienceCount] = useState(0);
  const [audienceModalOpen, setAudienceModalOpen] = useState(false);
  const [maxRecipients, setMaxRecipients] = useState<string>('');
  const [dryRun, setDryRun] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);

  const loadTemplates = async (sync = false) => {
    setTemplatesLoading(true);
    try {
      const rows = await fetchTemplates(provider, sync);
      setTemplates(rows);
      if (rows.length === 0) {
        notify(`No approved static templates found for ${provider}`, { type: 'warning' });
      }
    } catch (error) {
      console.error(error);
      notify('Failed to load templates', { type: 'error' });
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates(true);
    setSelectedTemplateId('');
    setSelectedTemplate(null);
    setPreview('');
  }, [provider]);

  useEffect(() => {
    if (!selectedTemplateId) {
      setSelectedTemplate(null);
      setPreview('');
      setTemplatePreviewLoading(false);
      return;
    }
    setTemplatePreviewLoading(true);
    fetchTemplate(Number(selectedTemplateId))
      .then((data) => {
        setSelectedTemplate(data.template);
        setPreview(data.preview);
      })
      .catch((error) => {
        console.error(error);
        notify('Failed to load template preview', { type: 'error' });
      })
      .finally(() => {
        setTemplatePreviewLoading(false);
      });
  }, [selectedTemplateId]);

  useEffect(() => {
    if (audienceFilters.customer_ids?.length) {
      setAudienceCount(audienceFilters.customer_ids.length);
      return;
    }
    previewAudience(audienceFilters)
      .then((result) => setAudienceCount(result.count))
      .catch(console.error);
  }, [audienceFilters]);

  const canNext = () => {
    if (activeStep === 0) return Boolean(provider);
    if (activeStep === 1) return Boolean(selectedTemplateId) && !templatesLoading;
    if (activeStep === 2) return audienceCount > 0;
    return true;
  };

  const handleLaunch = async () => {
    if (!selectedTemplateId) return;
    setSubmitting(true);
    try {
      const result = await createBroadcastJob({
        provider,
        template_id: Number(selectedTemplateId),
        audience_filter: audienceFilters,
        max_recipients: maxRecipients ? parseInt(maxRecipients, 10) : null,
        dry_run: dryRun,
      });
      notify(`Broadcast job created (${result.launch.mode})`, { type: 'success' });
      navigate(`/whatsapp-broadcasts/${result.job.id}`);
    } catch (error: any) {
      console.error(error);
      notify(error?.response?.data?.error || 'Failed to create broadcast job', { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Create WhatsApp Broadcast" />
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Select gateway</Typography>
          <RadioGroup value={provider} onChange={(e) => setProvider(e.target.value as BroadcastProvider)}>
            <FormControlLabel value="twilio" control={<Radio />} label="Twilio" />
            <FormControlLabel value="plivo" control={<Radio />} label="Plivo" />
          </RadioGroup>
        </Paper>
      )}

      {activeStep === 1 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Approved templates</Typography>
                <Button
                  size="small"
                  onClick={() => loadTemplates(true)}
                  disabled={templatesLoading}
                  startIcon={templatesLoading ? <CircularProgress size={14} /> : undefined}
                >
                  {templatesLoading ? 'Syncing...' : 'Sync'}
                </Button>
              </Box>

              {templatesLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Syncing approved templates from {provider}... this may take a few seconds.
                  </Typography>
                </Box>
              )}

              <FormControl fullWidth size="small" disabled={templatesLoading}>
                <InputLabel>Template</InputLabel>
                <Select
                  label="Template"
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value as number)}
                  displayEmpty
                  renderValue={(value) => {
                    if (templatesLoading) return 'Loading templates...';
                    if (!value) return 'Select a template';
                    const template = templates.find((row) => row.id === value);
                    return template
                      ? `${template.name} (${template.category || 'unknown'})`
                      : 'Select a template';
                  }}
                >
                  {!templatesLoading && templates.length === 0 && (
                    <MenuItem disabled value="">
                      No approved templates found — try Sync
                    </MenuItem>
                  )}
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name} ({template.category || 'unknown'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!templatesLoading && templates.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {templates.length} approved template{templates.length === 1 ? '' : 's'} available
                </Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <TemplatePreviewPanel
              template={selectedTemplate}
              preview={preview}
              loading={templatePreviewLoading}
            />
          </Grid>
        </Grid>
      )}

      {activeStep === 2 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Audience</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setAudienceFilters({ whatsapp_alert_subscribed: true });
                setAudienceCount(0);
              }}
            >
              WhatsApp subscribed only
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setAudienceFilters({});
                setAudienceCount(0);
              }}
            >
              All valid buyer phones
            </Button>
            <Button variant="contained" onClick={() => setAudienceModalOpen(true)}>
              Search / filter
            </Button>
          </Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Selected recipients: <strong>{audienceCount}</strong>
          </Typography>
          <TextField
            label="Max recipients (optional)"
            size="small"
            value={maxRecipients}
            onChange={(e) => setMaxRecipients(e.target.value)}
            helperText="Leave blank to use your selected audience (up to 5,000)"
            sx={{ mr: 2, width: 280 }}
          />
          <FormControlLabel
            control={<Checkbox checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
            label="Dry run (no API sends)"
          />
          <AudiencePickerModal
            open={audienceModalOpen}
            onClose={() => setAudienceModalOpen(false)}
            initialFilters={audienceFilters}
            onApply={(filters, count) => {
              setAudienceFilters(filters);
              setAudienceCount(count);
              setAudienceModalOpen(false);
            }}
          />
        </Paper>
      )}

      {activeStep === 3 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Review</Typography>
          <Typography>Gateway: {provider}</Typography>
          <Typography>Template: {selectedTemplate?.name}</Typography>
          <Typography>Recipients: {audienceCount}{maxRecipients ? ` (capped at ${maxRecipients})` : ''}</Typography>
          <Typography>Dry run: {dryRun ? 'Yes' : 'No'}</Typography>
          <Box sx={{ mt: 2 }}>
            <TemplatePreviewPanel template={selectedTemplate} preview={preview} />
          </Box>
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>Back</Button>
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" disabled={!canNext()} onClick={() => setActiveStep((s) => s + 1)}>
            Next
          </Button>
        ) : (
          <Button variant="contained" color="primary" disabled={submitting} onClick={handleLaunch}>
            Launch broadcast
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default WhatsAppBroadcastCreate;
